import {
  type ContractorDocumentAPI,
  type ContractorInvoiceAPI,
  ContractorInvoiceStateEnumAPI,
  type CreateInvoicePaymentResponseAPI,
  type CreateInvoiceQuoteResponseAPI,
  DocumentStatusEnum,
  DocumentTypeEnum,
  PermittedInvoiceCurrenciesEnum,
} from '@omnipresentgroup/types';

import { DEFAULT_COMPANY } from '../companies';
import {
  DEFAULT_PAYMENT,
  DEFAULT_PAYMENT_QUOTE,
  PAYMENT_QUOTE_WITH_BREAKDOWN,
} from '../contractorsPayments';

const generateDescription = (description?: string) =>
  description ??
  `Hey Mark,
  Attached is the invoice for the last detailed description of the invoice can go here.`;

export const DEFAULT_ATTACHMENT: ContractorDocumentAPI = {
  id: 'dummy-attachment-id',
  filename: 'Invoice-filename.pdf',
  status: DocumentStatusEnum.UPLOADED,
  type: DocumentTypeEnum.INVOICE,
};

const generateAttachment = (override: Partial<ContractorDocumentAPI> = {}) => ({
  ...DEFAULT_ATTACHMENT,
  ...override,
});

export const DEFAULT_CONTRACTOR_INVOICE: ContractorInvoiceAPI = {
  id: '9e0fefc5-d7b1-4adc-b1ec-8a714c8f78c4',
  invoice_ref: 'April 2023 invoice (includes all expenses)',
  currency: PermittedInvoiceCurrenciesEnum.USD,
  contractor: {
    id: 'fce93c94-3c9a-4705-9753-58688c710ee5',
    name: 'Test',
  },
  client: {
    id: DEFAULT_COMPANY.id,
    name: DEFAULT_COMPANY.name,
  },
  due_date: '2023-04-28',
  raised_at: '2023-04-20T06:49:45.391Z',
  paid_at: undefined,
  state: ContractorInvoiceStateEnumAPI.OPEN,
  amount: '5000',
  description: generateDescription(),
  attachments: [generateAttachment()],
  billing_currency: PermittedInvoiceCurrenciesEnum.USD,
};

export const generateInvoice = (
  override: Partial<ContractorInvoiceAPI> = {},
) => ({
  ...DEFAULT_CONTRACTOR_INVOICE,
  ...override,
});

export const PAID_INVOICE = generateInvoice({
  id: 'c6534a2f-b31c-4b1a-aa43-a22a2168cf33',
  invoice_ref: 'Jan correction invoice (2023)',
  due_date: '2023-01-29',
  raised_at: '2023-01-02T14:30:00',
  paid_at: '2023-01-29',
  amount: '150',
  state: ContractorInvoiceStateEnumAPI.PAID,
  payment_id: '608f471a-191a-48e0-8dd4-78219e85a52f',
});

export const OPEN_INVOICE = generateInvoice({
  id: 'd0bf48c6-3172-484c-89e0-4f053a08c3a5',
  invoice_ref: 'April 2022 Invoice',
  due_date: '2025-04-29',
  raised_at: '2025-03-29T14:30:00',
  amount: '1500',
  state: ContractorInvoiceStateEnumAPI.OPEN,
});

export const OPEN_INVOICE_WITH_EUR_CURRENCY = generateInvoice({
  id: 'eb18e024-ccf6-46b1-a26e-3e223fd965fb',
  invoice_ref: 'May 2025 Invoice',
  due_date: '2025-04-29',
  raised_at: '2025-03-29T14:30:00',
  amount: '1500',
  currency: PermittedInvoiceCurrenciesEnum.EUR,
  state: ContractorInvoiceStateEnumAPI.OPEN,
});

export const OPEN_INVOICE_WITH_EUR_BILLING_CURRENCY = generateInvoice({
  id: '98131f22-13b6-4848-ba80-3864535f59ab',
  invoice_ref: 'June 2025 Invoice',
  due_date: '2025-04-29',
  raised_at: '2025-03-29T14:30:00',
  amount: '1500',
  billing_currency: PermittedInvoiceCurrenciesEnum.EUR,
  state: ContractorInvoiceStateEnumAPI.OPEN,
});

export const OPEN_INVOICE_WITH_PAYMENT_ID = generateInvoice({
  id: 'd0bf48c6-3172-484c-89e0-4f053a0852a5',
  invoice_ref: 'April 2025 Invoice',
  due_date: '2025-04-29',
  raised_at: '2025-03-29T14:30:00',
  amount: '1500',
  state: ContractorInvoiceStateEnumAPI.OPEN,
  payment_id: '608f471a-191a-48e0-8dd4-78219e85a52f',
});

const OPEN_WITH_OVERDUE_INVOICE = generateInvoice({
  id: 'd0bf48c6-3172-484c-89e0-4f053a08c1a5',
  invoice_ref: 'April 2025 Invoice',
  due_date: '2025-04-29',
  raised_at: '2025-04-11T14:30:00',
  amount: '1500',
  overdue_by: '5',
  state: ContractorInvoiceStateEnumAPI.OPEN,
});

const CANCELLED_INVOICE = generateInvoice({
  id: '00a15683-f0cc-456a-ab59-32e1c6399ce1',
  invoice_ref: 'December 2023 Invoice',
  due_date: '2023-12-20',
  raised_at: '2023-12-20T14:30:00',
  amount: '1500',
  state: ContractorInvoiceStateEnumAPI.CANCELLED,
});

export const IN_PROGRESS_INVOICE = generateInvoice({
  id: '293cbdc8-40e6-49d0-b02e-31ebae41eb65',
  invoice_ref: 'Jan 2024 Invoice',
  due_date: '2024-01-15',
  raised_at: '2023-10-11T14:30:00',
  amount: '1500',
  state: ContractorInvoiceStateEnumAPI.IN_PROGRESS,
  payment_id: '608f471a-191a-48e0-8dd4-78219e85a52f',
});

export const IN_PROGRESS_INVOICE_WITH_OVERDUE = generateInvoice({
  id: '293cbdc8-40e6-49d0-b02e-33ebae41eb65',
  invoice_ref: 'Ana 2024 Invoice',
  due_date: '2024-01-15',
  raised_at: '2023-10-11T14:30:00',
  amount: '1500',
  state: ContractorInvoiceStateEnumAPI.IN_PROGRESS,
  payment_id: '608f471a-191a-48e0-8dd4-78219e85a52f',
  overdue_by: '8',
});

export const REJECTED_INVOICE = generateInvoice({
  id: '00a15683-f0cc-456a-ab59-32e1c6399ce2',
  invoice_ref: 'Rejected Invoice',
  due_date: '2023-12-20',
  raised_at: '2023-12-20T14:30:00',
  amount: '1500',
  state: ContractorInvoiceStateEnumAPI.REJECTED,
});

export const DEFAULT_CONTRACTORS_INVOICES = [
  DEFAULT_CONTRACTOR_INVOICE,
  PAID_INVOICE,
  OPEN_INVOICE,
  CANCELLED_INVOICE,
  IN_PROGRESS_INVOICE,
  OPEN_WITH_OVERDUE_INVOICE,
  IN_PROGRESS_INVOICE_WITH_OVERDUE,
  OPEN_INVOICE_WITH_PAYMENT_ID,
  REJECTED_INVOICE,
];

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_LENGTH = 1;

export const DEFAULT_CONTRACTORS_INVOICES_RESPONSE = {
  items: DEFAULT_CONTRACTORS_INVOICES,
  total: DEFAULT_CONTRACTORS_INVOICES.length,
  page: DEFAULT_PAGE_LENGTH,
  size: DEFAULT_PAGE_SIZE,
  pages: Math.ceil(DEFAULT_CONTRACTORS_INVOICES.length / DEFAULT_PAGE_SIZE),
};

export const DEFAULT_CONTRACTOR_INVOICES_QUOTE: CreateInvoiceQuoteResponseAPI =
  DEFAULT_PAYMENT_QUOTE;

export const CONTRACTOR_INVOICES_QUOTE_WITH_BREAKDOWN: CreateInvoiceQuoteResponseAPI =
  PAYMENT_QUOTE_WITH_BREAKDOWN;

export const DEFAULT_CONTRACTOR_INVOICES_PAYMENT_DETAILS: CreateInvoicePaymentResponseAPI =
  DEFAULT_PAYMENT;

export const DEFAULT_CONTRACTOR_INVOICES_REPORT = `
Invoice,Contractor,Contractor ID,Description,Cost Category,Invoice Raised date,Due date,Paid on date,Quantity,Original amount,Original currency,Converted amount,Currency,VAT,"Total amount
(VAT included)",Payment ID,Reference,Status
#008 Mayo,Fran Hernandez,f5f302ac-7b46-4532-acfc-b0ae824f157a,Yet another amazing invoice.,Invoice,08/05/2024,22/05/2024,,1,800,EUR,892.5600,USD,,892.5600,5194c8ee08144fe882244b144e2cefc0,MG1wz17ibt,PAID
#008 Mayo,Fran Hernandez,f5f302ac-7b46-4532-acfc-b0ae824f157a,Yet another amazing invoice.,Management fee,08/05/2024,22/05/2024,,1,,,0,USD,,0,5194c8ee08144fe882244b144e2cefc0,MG1wz17ibt,PAID
#0004 Mayo,Fran Hernandez,f5f302ac-7b46-4532-acfc-b0ae824f157a,N/A,Invoice,08/05/2024,15/05/2024,08/05/2024,1,1000,EUR,,USD,,,b9727eced66d4b14b594314bb2935456,,PAID
#0004 Mayo,Fran Hernandez,f5f302ac-7b46-4532-acfc-b0ae824f157a,N/A,Management fee,08/05/2024,15/05/2024,08/05/2024,1,,,,USD,,,b9727eced66d4b14b594314bb2935456,,PAID
`;
